/**
 * FeedbackModal Component
 *
 * Modal for submitting feedback on a query/message.
 * Allows users to select issue type and add comments.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Turtle, XCircle, Lightbulb, MessageSquare, Send, Loader } from 'lucide-react';
import { useFeedback } from '../hooks/useFeedback';
import { useSettings } from '../hooks/useSettings';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled(motion.div)`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
  border-radius: 16px 16px 0 0;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Content = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 12px;
`;

const IssueTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const IssueTypeButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border: 2px solid ${props => props.$selected ? '#1A8B8B' : '#e2e8f0'};
  border-radius: 12px;
  background: ${props => props.$selected ? 'rgba(26, 139, 139, 0.1)' : 'white'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #1A8B8B;
    background: rgba(26, 139, 139, 0.05);
  }

  .icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => {
      switch (props.$type) {
        case 'too_slow': return 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)';
        case 'wrong_answer': return 'linear-gradient(135deg, #fc8181 0%, #f56565 100%)';
        case 'could_be_better': return 'linear-gradient(135deg, #68d391 0%, #48bb78 100%)';
        case 'other': return 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)';
        default: return '#e2e8f0';
      }
    }};
    color: white;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .label {
    font-size: 13px;
    font-weight: 500;
    color: ${props => props.$selected ? '#1A8B8B' : '#4a5568'};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 14px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  &:focus {
    outline: none;
    border-color: #1A8B8B;
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

const CharCount = styled.div`
  font-size: 12px;
  color: ${props => props.$isError ? '#e53e3e' : '#a0aec0'};
  text-align: ${props => props.$isRTL ? 'left' : 'right'};
  margin-top: 6px;
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled(motion.button)`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
    color: white;
    border: none;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(26, 139, 139, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: #4a5568;
    border: 1px solid #e2e8f0;

    &:hover {
      background: #f7fafc;
    }
  `}

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  border: 1px solid #feb2b2;
  color: #c53030;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const SuccessMessage = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;

  .icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, #68d391 0%, #48bb78 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    color: white;

    svg {
      width: 32px;
      height: 32px;
    }
  }

  .title {
    font-size: 18px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 8px;
  }

  .message {
    font-size: 14px;
    color: #718096;
  }
`;

const ISSUE_TYPES = [
  { id: 'too_slow', icon: Turtle, label_ar: 'بطيء جداً', label_en: 'Too Slow' },
  { id: 'wrong_answer', icon: XCircle, label_ar: 'إجابة خاطئة', label_en: 'Wrong Answer' },
  { id: 'could_be_better', icon: Lightbulb, label_ar: 'يمكن تحسينها', label_en: 'Could Be Better' },
  { id: 'other', icon: MessageSquare, label_ar: 'أخرى', label_en: 'Other' }
];

function FeedbackModal({ isOpen, onClose, messageId, sessionId }) {
  const [selectedType, setSelectedType] = useState(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { createFeedback, loading, error, setError } = useFeedback();
  const { language, isRTL } = useSettings();

  const minCommentLength = 10;
  const maxCommentLength = 2000;
  // Comment is required for "Other" and "Could Be Better" types - for others it's optional
  const commentRequired = selectedType === 'other' || selectedType === 'could_be_better';
  const isCommentValid = commentRequired
    ? (comment.length >= minCommentLength && comment.length <= maxCommentLength)
    : (comment.length === 0 || comment.length <= maxCommentLength);
  const canSubmit = selectedType && isCommentValid && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      await createFeedback(messageId, sessionId, selectedType, comment);
      setSubmitted(true);

      // Auto close after success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    setComment('');
    setSubmitted(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <Modal
          $isRTL={isRTL}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Header>
            <Title>
              {language === 'ar' ? 'الإبلاغ عن مشكلة' : 'Report Issue'}
            </Title>
            <CloseButton onClick={handleClose}>
              <X />
            </CloseButton>
          </Header>

          {submitted ? (
            <SuccessMessage
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="icon">
                <Send />
              </div>
              <div className="title">
                {language === 'ar' ? 'تم الإرسال بنجاح!' : 'Submitted Successfully!'}
              </div>
              <div className="message">
                {language === 'ar'
                  ? 'شكراً لك! سيتم مراجعة البلاغ قريباً.'
                  : 'Thank you! Your report will be reviewed soon.'}
              </div>
            </SuccessMessage>
          ) : (
            <>
              <Content>
                {error && <ErrorMessage>{error}</ErrorMessage>}

                <Section>
                  <Label>
                    {language === 'ar' ? 'نوع المشكلة' : 'Issue Type'}
                  </Label>
                  <IssueTypeGrid>
                    {ISSUE_TYPES.map(type => {
                      const Icon = type.icon;
                      return (
                        <IssueTypeButton
                          key={type.id}
                          $type={type.id}
                          $selected={selectedType === type.id}
                          onClick={() => setSelectedType(type.id)}
                        >
                          <div className="icon">
                            <Icon />
                          </div>
                          <span className="label">
                            {language === 'ar' ? type.label_ar : type.label_en}
                          </span>
                        </IssueTypeButton>
                      );
                    })}
                  </IssueTypeGrid>
                </Section>

                <Section>
                  <Label>
                    {language === 'ar' ? 'وصف المشكلة' : 'Describe the Issue'}
                    {commentRequired && <span style={{ color: '#e53e3e', marginRight: '4px', marginLeft: '4px' }}>*</span>}
                    {!commentRequired && <span style={{ color: '#a0aec0', fontSize: '12px', marginRight: '8px', marginLeft: '8px' }}>
                      ({language === 'ar' ? 'اختياري' : 'optional'})
                    </span>}
                  </Label>
                  <TextArea
                    $isRTL={isRTL}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={commentRequired
                      ? (language === 'ar'
                        ? 'اشرح المشكلة بالتفصيل وكيف تتوقع أن يكون الحل...'
                        : 'Explain the issue in detail and how you expect it to be solved...')
                      : (language === 'ar'
                        ? 'أضف تفاصيل إضافية إذا رغبت...'
                        : 'Add additional details if you want...')}
                    maxLength={maxCommentLength}
                  />
                  {commentRequired && (
                    <CharCount
                      $isRTL={isRTL}
                      $isError={comment.length > 0 && comment.length < minCommentLength}
                    >
                      {comment.length}/{maxCommentLength}
                      {comment.length > 0 && comment.length < minCommentLength && (
                        <span> ({language === 'ar' ? `${minCommentLength - comment.length} حرف على الأقل` : `${minCommentLength - comment.length} more chars needed`})</span>
                      )}
                    </CharCount>
                  )}
                </Section>
              </Content>

              <Footer>
                <Button onClick={handleClose}>
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                  $primary
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  whileHover={canSubmit ? { scale: 1.02 } : {}}
                  whileTap={canSubmit ? { scale: 0.98 } : {}}
                >
                  {loading ? (
                    <>
                      <Loader className="animate-spin" />
                      {language === 'ar' ? 'جاري الإرسال...' : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      <Send />
                      {language === 'ar' ? 'إرسال' : 'Submit'}
                    </>
                  )}
                </Button>
              </Footer>
            </>
          )}
        </Modal>
      </Overlay>
    </AnimatePresence>
  );
}

export default FeedbackModal;
